import { z } from 'zod';
import { jobTypeSchema } from './enums.js';
/**
 * Filtre « Méthode » (type de job) du listing, accepté en CSV depuis la
 * querystring. Un GROUPE d'UI peut couvrir plusieurs `job_type` (ex. « EXIF »
 * = `exif_publish,exif_erase` ; « Dédup » = `dedup,dedup_apply`) → on accepte
 * une LISTE plutôt qu'une valeur unique. Même technique que `statesFilterSchema`
 * (`asset-query.ts`) : `z.preprocess` (split CSV) + `.meta({type:'string'})` pour
 * qu'AJV traite le champ comme une STRING (sinon il rejetterait `"a,b"`), Zod
 * re-valide chaque valeur ∈ enum. `.transform` proscrit (`z.toJSONSchema` lève).
 */
const splitJobTypesCsv = (value) => {
    if (typeof value !== 'string')
        return value;
    const parts = value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    return [...new Set(parts)];
};
export const jobTypesFilterSchema = z
    .preprocess(splitJobTypesCsv, z.array(jobTypeSchema).nonempty())
    .meta({ type: 'string', minItems: undefined, items: undefined })
    .optional();
/**
 * Contrats du JOURNAL DES ERREURS DE TRAITEMENT DE FICHIERS (Lot A).
 * Source unique de vérité front + back.
 *
 * Une ligne = un échec d'opération sur un fichier détecté par un job (scan pour le
 * Lot A ; move/dédup/EXIF/vignettes = Lot B). Rend visibles et consultables des
 * échecs jusqu'ici silencieux (PDF nommé .png, image corrompue, fichier illisible,
 * échec de vignette…). `userEmail` est en clair (exigence, cf. `request_logs`) ;
 * aucun secret / corps / query-string n'y figure — uniquement des métadonnées.
 */
/** Borne haute de la fenêtre de listing (protège la DB + le rendu). */
export const FILE_OP_ERRORS_MAX_LIMIT = 200;
export const FILE_OP_ERRORS_DEFAULT_LIMIT = 50;
/** Longueur maximale du message journalisé (borne la taille des lignes). */
export const FILE_OP_ERROR_MESSAGE_MAX = 500;
/**
 * Gravité de l'erreur.
 * - `error`   : opération échouée (fichier non traité, hors résilience normale).
 * - `skipped` : fichier ignoré par le job (illisible / non média / non traitable) —
 *               la résilience du scan continue, mais on trace pourquoi.
 * - `warning` : anomalie non bloquante (traité malgré tout, mais suspect).
 */
export const fileOpErrorSeveritySchema = z.enum(['error', 'skipped', 'warning']);
/**
 * Phase de l'opération qui a échoué.
 * - `read`      : lecture/accès disque du fichier.
 * - `decode`    : décodage/parsing du média (format invalide, PDF nommé .png…).
 * - `thumbnail` : génération de vignette.
 * - `write`     : écriture (EXIF publish/erase, déplacement) — Lot B.
 * - `delete`    : suppression (dédup apply) — Lot B.
 * - `probe`     : sonde de flux (ffprobe « avec son ») — Lot B.
 * - `catalog`   : fichier VU au parcours du scan mais NON indexé car non média /
 *                 extension non reconnue / type incohérent (réconciliation
 *                 disque↔index). N'est PAS une erreur de traitement d'un média,
 *                 mais une raison d'exclusion tracée (rend l'écart explicable).
 * - `other`     : phase indéterminée / non classée.
 */
export const fileOpErrorOperationSchema = z.enum([
    'read',
    'decode',
    'thumbnail',
    'write',
    'delete',
    'probe',
    'catalog',
    'other',
]);
/**
 * Item de journal (`GET /api/libraries/:id/file-errors`). Champs contextuels tous
 * optionnels sauf `message` (le job connaît toujours au moins ce qui a échoué).
 */
export const fileOpErrorSchema = z.object({
    id: z.uuid(),
    ts: z.string().datetime({ offset: true }),
    /** Le run (job) qui a produit l'erreur, ou `null`. */
    jobId: z.uuid().nullable(),
    /** Type du job producteur (Lot A : `scan`). */
    jobType: jobTypeSchema,
    operation: fileOpErrorOperationSchema,
    severity: fileOpErrorSeveritySchema,
    /** Utilisateur ayant lancé le job, ou `null`. */
    userId: z.uuid().nullable(),
    /** E-mail de l'utilisateur, en clair, ou `null`. */
    userEmail: z.string().nullable(),
    /** Asset concerné s'il est connu (déjà indexé), sinon `null`. */
    assetId: z.uuid().nullable(),
    /** Chemin relatif à la racine de la bibliothèque (fichier pas encore indexé). */
    relPath: z.string().nullable(),
    /** Vrai type détecté par le contenu (ex. `application/pdf`), rend visible le
     *  « PDF nommé .png ». `null` si non déterminé (échec avant la lecture EXIF). */
    detectedMime: z.string().nullable(),
    /** Code d'erreur système/applicatif (`ENOENT`/`EACCES`/`EROFS`/`DECODE`…). */
    errorCode: z.string().nullable(),
    /** Message d'erreur (tronqué à FILE_OP_ERROR_MESSAGE_MAX côté insertion). */
    message: z.string(),
});
/**
 * Query params de `GET /api/libraries/:id/file-errors`. `limit` borné (1..200,
 * défaut 50), `offset` ≥ 0. Trois filtres optionnels (axes de l'UI Lot C) :
 * utilisateur / gravité / type de job. Coercition depuis la querystring via
 * `z.coerce.number()` (AJV coerce, Zod re-valide — cf. `requestLogQuerySchema`).
 */
export const fileOpErrorsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(FILE_OP_ERRORS_MAX_LIMIT)
        .default(FILE_OP_ERRORS_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().default(0),
    userId: z.uuid().optional(),
    severity: fileOpErrorSeveritySchema.optional(),
    /** Type(s) de job producteur, en CSV (un groupe d'UI peut en couvrir plusieurs). */
    jobType: jobTypesFilterSchema,
});
/** Page de journaux (`{ errors, total }`) — `total` dimensionne la pagination. */
export const fileOpErrorsResultSchema = z.object({
    errors: z.array(fileOpErrorSchema),
    total: z.number().int().nonnegative(),
});
/**
 * Corps de `DELETE /api/libraries/:id/file-errors`. `before` (ISO 8601) optionnel :
 * absent → purge TOUT le journal de la bibliothèque ; présent → purge les entrées
 * `ts < before`. Le client enverra la FIN de la journée sélectionnée.
 */
export const purgeFileOpErrorsRequestSchema = z.object({
    before: z.string().datetime({ offset: true }).optional(),
});
/** Résultat de purge : nombre de lignes supprimées. */
export const purgeFileOpErrorsResultSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
//# sourceMappingURL=file-errors.js.map